/**
 * One way to build and download a CSV export (PM 30a1448a).
 *
 * Every "Export CSV" button used to serialize its rows by hand, each with
 * its own quoting (one quoted on commas and quotes but not newlines, so a
 * note with a line break broke the file; one quoted every field; two went
 * through Papa.unparse). The text is now @mediatechgroup/tabular's toCsv:
 * RFC 4180 quoting, CRLF line endings, a trailing newline, and the
 * formula-injection guard on every cell (a cell starting with `=`, `@`, a
 * tab, or a `+`/`-` not followed by a number is written with a leading
 * apostrophe, so a spreadsheet shows it as text instead of running it).
 * The rows and the columns are each export's own.
 *
 * What the pre-adoption serializers produced is pinned in
 * src/utils/__tests__/csvExport.pinned.test.ts: what a spreadsheet reads back
 * is the same for every cell that is not a formula; the guard and the line
 * endings are the listed differences.
 */
import { toCsv } from "@mediatechgroup/tabular";
import type { CsvColumn } from "@mediatechgroup/tabular";

export type { CsvColumn };

/**
 * The CSV text for `rows`. Without `columns`, the first row's keys are the
 * columns and their labels, as Papa.unparse and the hand-rolled exports
 * read them.
 */
export function csvText(rows: Record<string, unknown>[], columns?: CsvColumn[]): string {
  const cols = columns ?? Object.keys(rows[0] ?? {}).map((key) => ({ key, label: key }));
  return toCsv(rows, cols);
}

/** Hand `text` to the browser as a file download named `filename`. */
export function downloadCsv(filename: string, text: string): void {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Build and download in one call. */
export function exportCsv(filename: string, rows: Record<string, unknown>[], columns?: CsvColumn[]): void {
  downloadCsv(filename, csvText(rows, columns));
}
